<template>
  <TemplateBase :page="page">
    <template #notifications>
      <!-- Slot with notifications in the header if needed -->
      <MoleculeNotification :message="get(page, 'production.urgentMessage')" />
    </template>

    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="true" />
    </template>

    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeEventDetailPageBackButton :theme="theme" />
    </template>

    <div class="relative z-0 lg:grid lg:grid-cols-1">
      <!--Event Detail Header -->
      <MoleculeEventDetailPageHeaderV2
        class="mb-6"
        :title="get(page, 'production.title')"
        :image="get(page, 'production.image')"
        :image-alt="get(page, 'production.image.copyright')"
        :theme="theme"
        :audio-works="get(page, 'production.works')"
        :header-audio-is-playing="headerAudioIsPlaying"
        :toggle-header-audio="toggleHeaderAudio"
      />

      <!--Green event info block, only visible on large screens -->
      <section class="event-detail-info pointer-events-none hidden relative z-10 lg:block lg:row-start-2 w-full lg:col-span-full lg:pr-8 xl:pr-24 mx-auto">
        <aside
          class="event-detail-info__data-desktop pointer-events-auto lg:sticky z-10 lg:w-[440px] ml-auto rounded-b-2xl p-6 lg:p-5 sticky-element-desktop shadow-md"
          :class="[{ 'bg-app-secondary-8-green--vista-blue': !theme }, `t-cdp-background-${theme}`]"
        >
          <MoleculeEventDetailPageInfoDataV2
            :url="page.purchaseUrl"
            :stream-url="page.streamUrl"
            :event-date="toDate(page.eventDate)"
            :event-end-date="toDate(page.eventEndDate)"
            :location="get(page, 'room.title')"
            :price="minPrice"
            :date="toDate(page.eventDate)"
            :theme="theme"
            :is-favorite="isFavorite"
            :additional-events="additionalEvents"
            :tix-sale="tixSale"
            :concert-status="concertStatus"
            :concert-state="concertState"
            @toggle-favorite="favoritesEnabled ? favoritesToggleEventId() : favoritesOpenOverlay(page.iConcertId)"
            @order-click="orderClick"
          />
        </aside>
      </section>

      <!--  separate container with the next section of the page that doesn't belong to the header,
            it contains the body, the mobile event info block, the sticky event info block and all other page sections -->
      <section class="lg:row-start-2 lg:col-span-full">
        <!--Mobile event info block -->
        <div class="event-detail-info__data-mobile event-detail-info__data-mobile--static z-20 lg:hidden">
          <MoleculeEventDetailPageInfoDataV2
            class="event-detail-info__data-mobile__content md:px-12 md:col-start-2 md:col-span-10 lg:hidden mb-6"
            :url="page.purchaseUrl"
            :stream-url="page.streamUrl"
            :event-date="toDate(page.eventDate)"
            :event-end-date="toDate(page.eventEndDate)"
            :location="get(page, 'room.title')"
            :price="minPrice"
            :theme="theme"
            :date="toDate(page.eventDate)"
            :is-favorite="isFavorite"
            :additional-events="additionalEvents"
            :tix-sale="tixSale"
            :concert-status="concertStatus"
            :concert-state="concertState"
            @order-click="orderClick"
            @toggle-favorite="favoritesEnabled ? favoritesToggleEventId() : favoritesOpenOverlay(page.iConcertId)"
          />
        </div>

        <!--Mobile sticky event info block -->
        <div
          class="event-detail-info__data-mobile event-detail-info__data-mobile--sticky i-is-stuck-on-mobile sticky hidden z-20 bg-black text-white shadow-xl rounded-b-2xl p-4 mx-0"
        >
          <MoleculeEventDetailPageInfoDataMobileSticky
            :event-date="toDate(page.eventDate)"
            :title="get(page, 'production.title')"
            :url="page.purchaseUrl"
            :stream-url="page.streamUrl"
            :tix-sale-is-available="tixSale.saleIsAvailable.value"
            :concert-state="concertState"
            :theme="theme"
            @order-click="orderClick"
          />
        </div>

        <!-- Body -->
        <MoleculeEventDetailPageBodyV2
          class="mb-10"
          :title="get(page, 'production.title')"
          :introduction="get(page, 'production.introduction')"
          :body="get(page, 'production.description')"
          :organizer="get(page, 'production.organizer.title')"
          :genres="get(page, 'production.genres')"
          :part-of-series="page.series"
          :theme="theme"
          :sponsors="get(page, 'production.sponsors')"
          :audio-works="get(page, 'production.works')"
          :header-audio-is-playing="headerAudioIsPlaying"
          :toggle-header-audio="toggleHeaderAudio"
        >
          <!-- Program -->
          <template #program>
            <MoleculeProgramEventDetail
              :musicians="get(page, 'production.musicians')"
              :substitute="get(page, 'production.substitute')"
              :audio-works="get(page, 'production.works')"
              :has-intermission="get(page, 'production.hasIntermission')"
              :intermission-description="get(page, 'production.intermissionDescription')"
              :is-amplified-concert="get(page, 'production.isAmplifiedConcert')"
              :header-audio-is-playing="headerAudioIsPlaying"
              :toggle-header-audio="toggleHeaderAudio"
              :program="get(page, 'production.program')"
              :theme="theme"
            />
          </template>

          <!-- SideProgram -->
          <template #sideProgram>
            <MoleculeEventDetailPageSideProgramV2 v-if="page.sidePrograms && page.sidePrograms.length" class="my-8 lg:w-[calc(100%-340px)]" :side-programs="page.sidePrograms" />
          </template>
        </MoleculeEventDetailPageBodyV2>

        <!-- Info and tickets -->
        <MoleculeEventDetailPageInfoAndTicketsV2
          class="mb-16"
          :room="get(page, 'room.title')"
          :prices="page.pricing"
          :additional-events="additionalEvents"
          :concert-status="page.concertStatus"
        >
          <MoleculeEventDetailPageAdditionalInfoV2
            class="text-sm pb-2 sm:pb-4 xl:pb-6"
            :additional-cost-description="get(page, 'production.additionalCostDescription')"
            :beverages="get(page, 'production.hasBeverageIncluded')"
            :beverage-description="get(page, 'production.beverageDescription')"
            :show-vriendenloterij-vip-card-banner="showVriendenloterijVipCardBanner"
          />
        </MoleculeEventDetailPageInfoAndTicketsV2>
      </section>

      <!-- Media -->
      <MoleculeEventDetailPageMedia :images="get(page, 'production.images', [])" :videos="get(page, 'production.youtubes', [])" />

      <!-- Discover -->
      <MoleculeEventDetailPageDiscover v-if="page.linkedDiscoveries && page.linkedDiscoveries.length" :entries="page.linkedDiscoveries" />

      <!-- NETA -->
      <MoleculeEventDetailPageNeta :production="page.production" :related-event-uris="page.relatedEvents" />

      <!-- Temporary AA test to compare GA variation events with VWO -->
      <MoleculeABTestWrapper experiment-code="eventDetailAaFeatureFlag">
        <template #A>
          <div id="A"></div>
        </template>
        <template #B>
          <div id="B"></div>
        </template>
      </MoleculeABTestWrapper>
      <!-- End Temporary AA test to compare GA variation events with VWO -->
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import { toDate } from '~/assets/util'
import { dateInPast } from '@/assets/date'
import { createMusicEventFromPage } from '@/assets/structured-data/music-event'

const props = defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

// track the page view
setupGtmViewItem(props.page)
setupGtmCdpEvent('CV07 - V2')
setupHotjar('CV07_V2')

const favoritesStore = useFavoritesStore()
const gtmCdpAction = setupGtmCdpAction()

// favorites
const favoriteEventIConcertIds = computed(() => favoritesStore.eventIConcertIds)
const isFavorite = computed(() => favoriteEventIConcertIds.value.includes(props.page.iConcertId))
const favoritesEnabled = computed(() => favoritesStore.enabled)

function favoritesToggleEventId() {
  if (!isFavorite.value) {
    gtmCdpAction('CV07 - Save this concert clicked')
  }
  favoritesStore.toggleEventIConcertId(props.page.iConcertId)
}
function favoritesOpenOverlay(eventIConcertId: number) {
  favoritesStore.openOverlayAndStoreId(eventIConcertId)
}

// Unlike the V1 template, V2 does not display the "has-vriendenloterij-ticket-type" prop anywhere, so
// `hasVriendenloterijTicketType` is dropped here (was a dead Options API setup() return before this
// conversion, masked because Options API doesn't flag unused return values).
const { showVriendenloterijVipCardBanner, minPrice } = setupTixPricing(props.page.pricing, props.page.eventCustomerTags, props.page.tags)
const tixSale = setupTixSale(props.page)
const concertStatus = setupConcertStatus(props.page.concertStatus, 'event-detail')
// TODO: Create setup function and possible merge with setupConcertStatus
const concertState = computed(() => ({
  EXPIRED: dateInPast(toDate(props.page.eventEndDate) || toDate(props.page.eventDate), new Date()),
  SOLD_OUT: tixSale.saleTemplate.value === 'tix-sale-status-sold-out',
  MOVED: props.page.concertStatus === 'Verplaatst',
  CANCELED: props.page.concertStatus === 'Geannuleerd',
}))
const hasLastTicketsStatus = tixSale?.saleTemplate.value === 'tix-sale-status-few-tickets'

setupGtmCdpConcertStatus(concertState.value, hasLastTicketsStatus)

// JSON-LD MusicEvent structured data. Server-only: structured data is consumed from the rendered HTML by
// crawlers, and useHead drops the inline script on client hydration; useServerHead emits it once at SSR,
// when the tix price/sale refs are already resolved.
useServerHead({
  script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(createMusicEventFromPage(props.page, minPrice, tixSale)) }],
})

const orderClick = setupGtmClickOrderTickets(props.page)
const theme = props.page.production?.theme

// Audio
const headerAudioIsPlaying = ref(false)

function toggleHeaderAudio(isPlaying: boolean) {
  headerAudioIsPlaying.value = isPlaying
}

// Sticky element
const func = debounce(() => {
  const mobileStatic = document.querySelector('.event-detail-info__data-mobile--static')
  const mobileSticky = document.querySelector('.event-detail-info__data-mobile--sticky')
  // Both are rendered conditionally: on a page without them there is nothing to position.
  if (!mobileStatic || !mobileSticky) {
    return
  }
  const mobileDisplay = window.getComputedStyle(mobileStatic).display

  if (mobileDisplay !== 'none') {
    const mobileStaticPos = mobileStatic.getBoundingClientRect()

    if (mobileStaticPos.bottom < 0) {
      mobileSticky.classList.remove('hidden')
      mobileSticky.classList.add('visible')
    } else {
      mobileSticky.classList.remove('visible')
      mobileSticky.classList.add('hidden')
    }
  } else {
    const element = document.querySelector('.sticky-element-desktop')
    if (!element?.parentElement) {
      return
    }
    const elementPos = element.getBoundingClientRect()
    const parent = element.parentElement.getBoundingClientRect()

    if (parent.top < elementPos.top) {
      element.classList.add('i-is-stuck-on-desktop')
    } else {
      element.classList.remove('i-is-stuck-on-desktop')
    }
  }
}, 25)

// Make sure that the event listener is also removed, otherwise they will accumulate
onMounted(() => window.addEventListener('scroll', func))
onUnmounted(() => window.removeEventListener('scroll', func))

// Additional events
// Compute the additionalEvents, i.e. events in the production except the current one
const additionalEvents = computed(() => props.page.production?.events?.filter((event: CmsData) => event.url !== props.page.url))
</script>

<style lang="scss">
// scoped scss is not suitable for this component, it won't work in this case because it needs to inherit styles from parent elements

.event-detail-info {
  @screen 2xl {
    padding-left: 8vw;
    padding-right: 8vw;
  }
}

// sticky element class ...... TODO: add more info about how it works....
.event-detail-info__data-mobile {
  @apply top-0 mx-4;

  @screen sm {
    @apply mx-12;
  }

  @screen lg {
    @apply mx-0;
  }

  // This is when the info & data block becomes sticky on mobile
  //&.i-is-stuck-on-mobile {
  //  @apply bg-black text-white shadow-xl rounded-b-2xl p-4 mx-0;
  //
  //  @screen lg {
  //    @apply hidden;
  //  }
  //
  //  .event-detail-info__data-mobile__content {
  //    @apply flex items-center justify-between mb-0;
  //
  //    @screen sm {
  //      @apply px-2;
  //    }
  //
  //    @screen md {
  //      @apply col-span-full;
  //    }
  //  }
  //}
}

.event-detail-info__data-desktop {
  top: theme('spacing.32');

  @screen xl {
    top: theme('spacing.40');
  }

  // This is when the info & data block becomes sticky at desktop
  &.i-is-stuck-on-desktop {
    @apply rounded-2xl shadow-xl;
  }
}
</style>
